/**
 * Central endpoint registry — the single home for every support-api path
 * (projects-app parity: `apps/projects/src/utils/constants/api.constant.ts`).
 *
 * Every `*.api.ts` composes URLs as `${supportBase()}${SUPPORT_ENDPOINTS.X}`,
 * appending dynamic segments at the call site (`.../${id}/assign`), exactly the
 * way projects uses `API_ENDPOINTS`. No api file writes a raw path literal —
 * a renamed backend route is a one-line change here, and a typo'd constant is
 * a compile error instead of a silent 404.
 *
 * Paths are workspace-relative: `supportBase()` (lib/base.ts) prefixes the
 * active workspace slug (`/:slug/support`), so entries here start at the
 * support router root.
 */
export const SUPPORT_ENDPOINTS = {
  // Tickets
  TICKETS: '/tickets',
  TICKETS_COUNTS: '/tickets/counts',
  TICKETS_EXPORT: '/tickets/export',
  TICKETS_STATS: '/tickets/stats',

  // Activity feed + saved activity views
  ACTIVITY: '/activity',
  ACTIVITY_VIEWS: '/activity/views',

  // Approvals
  APPROVAL_CHAINS: '/approval-chains',

  // Dashboard (home composite + per-widget aggregates)
  DASHBOARD_ANALYTICS: '/dashboard/analytics',
  DASHBOARD_BREACHES: '/dashboard/analytics/breaches',
  DASHBOARD_APPROVALS: '/dashboard/approvals',
  DASHBOARD_DRIFT: '/dashboard/drift',
  DASHBOARD_HOME: '/dashboard/home',
  DASHBOARD_MY_PULSE: '/dashboard/my-pulse',
  DASHBOARD_OPS: '/dashboard/ops',
  DASHBOARD_STATS: '/dashboard/stats',
  DASHBOARD_TRANSFERS: '/dashboard/transfers',
  DASHBOARD_WATCHED: '/dashboard/watched',

  // Departments (workspace-owned, read here for pickers)
  DEPARTMENTS: '/departments',

  // Escalation ladders
  ESCALATION_CHAINS: '/escalation-chains',

  // Master lists + options
  MASTERS: '/masters',
  MASTERS_OPTIONS: '/masters/options',

  // Me (identity-scoped)
  ME_AVAILABILITY: '/me/availability',
  ME_AVAILABILITY_HANDOVER: '/me/availability/handover',
  ME_PENDING_APPROVALS: '/me/pending-approvals',
  ME_PERMISSIONS: '/me/permissions',
  ME_TEAMS: '/me/teams',

  // Notices
  NOTICES_ACTIVE: '/notices/active',

  // Notifications (+ WS connect token)
  NOTIFICATIONS: '/notifications',
  NOTIFICATIONS_CONNECT_TOKEN: '/notifications/connect-token',
  NOTIFICATIONS_READ_ALL: '/notifications/read-all',
  NOTIFICATIONS_UNREAD_COUNT: '/notifications/unread-count',

  // First-run onboarding
  ONBOARDING: '/onboarding',
  ONBOARDING_COMPLETE: '/onboarding/complete',
  ONBOARDING_PROVISION: '/onboarding/provision',

  // RBAC
  RBAC_AUDIT_LOGS: '/rbac/audit-logs',
  RBAC_ELEVATIONS: '/rbac/elevations',
  RBAC_PERMISSION_DEFINITIONS: '/rbac/permission-definitions',
  RBAC_TEMPLATES: '/rbac/templates',
  RBAC_USERS: '/rbac/users',
  RBAC_USERS_PERMISSIONS: '/rbac/users/permissions',
  RBAC_USERS_PERMISSIONS_BULK_ASSIGN: '/rbac/users/permissions/bulk-assign',

  // Resolution / handling config
  RESOLUTION_GLOBAL: '/resolution/global',
  RESOLUTION_GLOBAL_PICKUP: '/resolution/global-pickup',
  RESOLUTION_POLICY: '/resolution/policy',
  RESOLUTION_TEAMS: '/resolution/teams',

  // Desk settings
  SETTINGS: '/settings',
  BUSINESS_HOURS: '/business-hours',
  BUSINESS_HOURS_IMPACT: '/impact',
  BUSINESS_HOURS_HOLIDAYS: '/holidays',
  REGIONAL: '/regional',

  // Catalog: types → categories → SKUs
  SKUS: '/skus',
  SKUS_POPULAR: '/skus/popular',
  TICKET_CATEGORIES: '/ticket-categories',
  TICKET_TYPES: '/ticket-types',
  TICKET_TYPES_TREE: '/ticket-types/tree',

  // Teams (handler pools)
  TEAMS: '/teams',

  // Watcher rules
  WATCHER_RULES: '/watcher-rules',
} as const;

export type SupportEndpoint = (typeof SUPPORT_ENDPOINTS)[keyof typeof SUPPORT_ENDPOINTS];
